import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The exam countdown.
 *
 * Two decisions here matter more than the rest of the file.
 *
 * **It counts with `performance.now()`, not `Date.now()`.**
 * `performance.now()` is monotonic: it cannot be moved by changing the
 * system clock, and it does not jump when the OS syncs time. A candidate
 * who puts their clock back would otherwise buy themselves the
 * difference. `Date.now()` is also wrong in the boring case — a laptop
 * that suspends and resumes.
 *
 * **The server is the authority, and it re-asserts itself.**
 * The countdown here is only a smooth display. Every server response
 * carries `server_now` and `seconds_remaining`, and `syncFromServer`
 * re-anchors when the two disagree by more than a small tolerance. The
 * backend refuses writes past the deadline regardless of what this shows,
 * so the worst a broken clock achieves is a briefly wrong number on
 * screen.
 *
 * The tick is 500 ms against a fixed deadline rather than a decrementing
 * counter, so a throttled background tab catches up on its next tick
 * instead of accumulating drift. Same approach as `useIdleTimeout`.
 */

const TICK_MS = 500;

/** Re-anchor when the server disagrees by more than this. Below it, the
 *  difference is network latency and correcting would make the number
 *  visibly jitter. */
const DRIFT_TOLERANCE_SECONDS = 3;

export interface TestCountdown {
  /** Whole seconds left, floored at zero. */
  secondsRemaining: number;
  /** True once the deadline has passed on this client. */
  expired: boolean;
  /** Re-anchor from a server response. */
  syncFromServer: (secondsRemaining: number) => void;
}

export function useTestCountdown(
  initialSecondsRemaining: number | null,
  onExpire?: () => void,
  /**
   * Which clock this is. Changing it re-anchors.
   *
   * An exam has TWO clocks — the practice run's and the assessment's —
   * and only one runs at a time. Without this the hook, which anchors
   * once and then ignores its own prop, kept counting down the practice
   * deadline after the assessment began: the candidate saw four minutes
   * for a thirty-minute exam, and when that stale deadline elapsed the
   * expiry callback (wired during the assessment) ended the real thing.
   */
  phaseKey?: string,
): TestCountdown {
  const [secondsRemaining, setSecondsRemaining] = useState(
    initialSecondsRemaining ?? 0,
  );

  // Monotonic instant at which time runs out.
  const deadlineRef = useRef<number | null>(null);
  // Keep the callback fresh without re-arming the interval.
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);
  const firedRef = useRef(false);

  const anchor = useCallback((seconds: number) => {
    deadlineRef.current = performance.now() + seconds * 1000;
    if (seconds > 0) firedRef.current = false;
    setSecondsRemaining(Math.max(0, Math.floor(seconds)));
  }, []);

  // Anchor on the first real value FOR EACH PHASE. Within a phase,
  // corrections come through syncFromServer, so a re-render with the same
  // prop does not reset the clock the candidate is watching; across
  // phases the deadline is a different one and must be re-taken.
  const anchoredPhaseRef = useRef<string | null>(null);
  useEffect(() => {
    const key = phaseKey ?? "default";
    if (anchoredPhaseRef.current === key) return;
    if (initialSecondsRemaining == null) return;
    anchoredPhaseRef.current = key;
    anchor(initialSecondsRemaining);
  }, [initialSecondsRemaining, anchor, phaseKey]);

  const syncFromServer = useCallback(
    (serverSeconds: number) => {
      const deadline = deadlineRef.current;
      if (deadline == null) {
        anchor(serverSeconds);
        return;
      }
      const localSeconds = Math.max(
        0,
        (deadline - performance.now()) / 1000,
      );
      if (Math.abs(localSeconds - serverSeconds) > DRIFT_TOLERANCE_SECONDS) {
        anchor(serverSeconds);
      }
    },
    [anchor],
  );

  useEffect(() => {
    if (initialSecondsRemaining == null) return undefined;

    const id = window.setInterval(() => {
      const deadline = deadlineRef.current;
      if (deadline == null) return;
      const left = Math.max(0, Math.ceil((deadline - performance.now()) / 1000));
      setSecondsRemaining(left);
      if (left === 0 && !firedRef.current) {
        firedRef.current = true;
        onExpireRef.current?.();
      }
    }, TICK_MS);

    return () => window.clearInterval(id);
  }, [initialSecondsRemaining]);

  return {
    secondsRemaining,
    expired: secondsRemaining <= 0 && anchoredPhaseRef.current !== null,
    syncFromServer,
  };
}

/** ``h:mm:ss`` (or ``m:ss`` under an hour) for display. */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const mm = String(minutes).padStart(hours > 0 ? 2 : 1, "0");
  const ss = String(seconds).padStart(2, "0");
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}
